import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "./AdminShell";
import { api, MARKET_ORDER, MARKET_META } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Bell, History } from "lucide-react";

// --- Reminder helpers (kept from previous dashboard) ---------------------
const timeToMinutes = (t) => {
    if (!t) return null;
    const m = String(t).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (m[3].toUpperCase() === "AM") { if (h === 12) h = 0; }
    else { if (h !== 12) h += 12; }
    return h * 60 + min;
};
const nowISTMinutes = () => {
    const s = new Date().toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
    const [h, m] = s.split(":").map(Number);
    return h * 60 + m;
};
const computeReminder = (fr_time, sr_time) => {
    const n = nowISTMinutes();
    const fr = timeToMinutes(fr_time);
    let sr = timeToMinutes(sr_time);
    if (fr == null || sr == null) return { active: false };
    if (sr < fr) sr += 24 * 60;
    let cur = n;
    if (cur < 60 * 6 && sr > 24 * 60) cur += 24 * 60;
    const within = (t) => cur >= t - 2 && cur <= t + 2;
    if (within(fr)) return { active: true, phase: "F/R", mins: fr - cur };
    if (within(sr)) return { active: true, phase: "S/R", mins: sr - cur };
    return { active: false };
};

const todayIST = () => new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const prettyDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Single unified admin page to manage results across ALL games.
 * - Pick a date (defaults to today)
 * - Each game shows a compact row: FR input, SR input, Save, and Delete
 * - Recent-history strip at the bottom shows the latest ~14 saved rows across all games
 */
const AdminManageResults = () => {
    const [date, setDate] = useState(todayIST());
    const [rowsByKey, setRowsByKey] = useState({}); // { market_key: { id, fr, sr } }
    const [drafts, setDrafts] = useState({}); // { market_key: { fr, sr } }
    const [savingKey, setSavingKey] = useState(null);
    const [deletingKey, setDeletingKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);

    // Reminder tick
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 15 * 1000);
        return () => clearInterval(id);
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const [dayRes, histRes] = await Promise.all([
                // All results for the selected date
                api.get("/results", { params: { date, limit: 50 } }),
                // Recent history (last 14 rows across all games)
                api.get("/results", { params: { limit: 14 } }),
            ]);
            const map = {};
            (dayRes.data || []).forEach((r) => {
                map[r.market_key] = r;
            });
            setRowsByKey(map);
            // Reset drafts to match server state
            const d = {};
            MARKET_ORDER.forEach((k) => {
                d[k] = { fr: map[k]?.fr ?? "", sr: map[k]?.sr ?? "" };
            });
            setDrafts(d);
            setHistory(histRes.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

    const twoDigits = (v) => v.replace(/[^0-9]/g, "").slice(0, 2);

    const setDraft = (k, field, val) => {
        setDrafts((d) => ({ ...d, [k]: { ...d[k], [field]: twoDigits(val) } }));
    };

    const save = async (k) => {
        setSavingKey(k);
        try {
            const { fr, sr } = drafts[k] || {};
            await api.post("/results", {
                market_key: k,
                date,
                fr: fr === "" ? null : fr,
                sr: sr === "" ? null : sr,
            });
            toast.success(`${MARKET_META[k].name} saved`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSavingKey(null);
        }
    };

    const remove = async (k) => {
        const row = rowsByKey[k];
        if (!row?.id) return;
        if (!window.confirm(`Delete ${MARKET_META[k].name} for ${prettyDate(date)}?`)) return;
        setDeletingKey(k);
        try {
            await api.delete(`/results/${row.id}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally {
            setDeletingKey(null);
        }
    };

    const totalPublished = useMemo(() => Object.keys(rowsByKey).length, [rowsByKey]);

    return (
        <AdminShell title="Manage Results">
            <div className="flex flex-wrap items-end gap-3 justify-between">
                <div>
                    <h2 className="font-display font-bold text-xl text-blue-950 tracking-tight">
                        Manage Results
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">
                        Ek hi jagah pe sabhi games ke aaj ke aur pichle results manage karo.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500 font-semibold">Date</label>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        data-testid="admin-mr-date"
                        className="border border-slate-300 rounded-md px-3 py-2 text-sm font-num focus:border-blue-800 outline-none"
                    />
                </div>
            </div>

            {loading ? (
                <div className="mt-6 space-y-2">
                    {MARKET_ORDER.map((k) => <div key={k} className="h-14 skeleton rounded-lg" />)}
                </div>
            ) : (
                <div className="mt-5 space-y-2" data-testid="admin-mr-list">
                    {MARKET_ORDER.map((k) => {
                        const meta = MARKET_META[k];
                        const row = rowsByKey[k];
                        const d = drafts[k] || { fr: "", sr: "" };
                        const dirty = (d.fr ?? "") !== (row?.fr ?? "") || (d.sr ?? "") !== (row?.sr ?? "");
                        const reminder = computeReminder(meta.fr_time, meta.sr_time);
                        return (
                            <div
                                key={k}
                                className={`rounded-lg overflow-hidden bg-white border shadow-sm ${
                                    reminder.active ? "border-red-400 ring-2 ring-red-300/60 card-live" : "border-slate-200"
                                }`}
                                data-testid={`admin-mr-row-${k}`}
                            >
                                {/* Header */}
                                <div
                                    className="px-3 py-2 flex items-center gap-2 text-white text-xs sm:text-sm relative"
                                    style={{ background: meta.accent, color: meta.headerTextColor || "#FFFFFF" }}
                                >
                                    <span className="text-base leading-none" aria-hidden="true">{meta.emoji}</span>
                                    <span className="font-display font-bold tracking-tight">{meta.name}</span>
                                    <span className="ml-auto text-[10px] font-num opacity-90">
                                        F/R {meta.fr_time} · S/R {meta.sr_time}
                                    </span>
                                    {reminder.active && (
                                        <span
                                            className="absolute -top-1.5 left-3 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-500 text-white uppercase tracking-wider shadow-md ring-2 ring-white"
                                            title={`${reminder.phase} declaration due`}
                                        >
                                            <Bell className="h-2.5 w-2.5 live-dot" />
                                            {reminder.mins > 0 ? `${reminder.mins}m` : "Now"} · {reminder.phase}
                                        </span>
                                    )}
                                </div>

                                {/* Inputs row */}
                                <div className="grid grid-cols-12 items-center gap-2 px-3 py-2">
                                    <div className="col-span-4 sm:col-span-3">
                                        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">F/R</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={d.fr}
                                            onChange={(e) => setDraft(k, "fr", e.target.value)}
                                            placeholder="XX"
                                            data-testid={`admin-mr-fr-${k}`}
                                            className="mt-0.5 w-full border border-slate-300 rounded-md px-2 py-1.5 text-lg result-digit text-center focus:border-blue-800 outline-none"
                                        />
                                    </div>
                                    <div className="col-span-4 sm:col-span-3">
                                        <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">S/R</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={d.sr}
                                            onChange={(e) => setDraft(k, "sr", e.target.value)}
                                            placeholder="XX"
                                            data-testid={`admin-mr-sr-${k}`}
                                            className="mt-0.5 w-full border border-slate-300 rounded-md px-2 py-1.5 text-lg result-digit text-center focus:border-blue-800 outline-none"
                                        />
                                    </div>
                                    <div className="col-span-4 sm:col-span-3 flex items-center gap-2 sm:justify-start">
                                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Status</span>
                                        {row ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                                                Published
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                                                Awaiting
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-span-12 sm:col-span-3 flex items-center gap-2 justify-end">
                                        <button
                                            onClick={() => save(k)}
                                            disabled={savingKey === k || !dirty}
                                            data-testid={`admin-mr-save-${k}`}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-700 text-white text-xs font-semibold hover:bg-blue-800 disabled:opacity-40"
                                        >
                                            {savingKey === k ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                            Save
                                        </button>
                                        {row && (
                                            <button
                                                onClick={() => remove(k)}
                                                disabled={deletingKey === k}
                                                data-testid={`admin-mr-delete-${k}`}
                                                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-red-500 text-white text-xs font-semibold hover:bg-red-600 disabled:opacity-40"
                                                title="Delete this date's result"
                                            >
                                                {deletingKey === k ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Recent history */}
            <div className="mt-8">
                <div className="flex items-center gap-2 text-slate-700">
                    <History className="h-4 w-4" />
                    <h3 className="font-display font-bold text-sm tracking-tight">Recently Published</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                    Ye records automatically Previous Results mein bhi live hote hain — koi extra step nahi.
                </p>
                <div className="mt-3 rounded-lg border border-slate-200 overflow-hidden">
                    <table className="w-full text-xs sm:text-sm">
                        <thead className="bg-slate-100 text-slate-500 uppercase text-[10px] tracking-widest">
                            <tr>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Market</th>
                                <th className="px-3 py-2 text-right">F/R</th>
                                <th className="px-3 py-2 text-right">S/R</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.length === 0 ? (
                                <tr><td colSpan={4} className="p-6 text-center text-slate-400">No results yet.</td></tr>
                            ) : (
                                history.map((r) => (
                                    <tr key={r.id} className="border-t border-slate-100" data-testid={`admin-mr-hist-${r.id}`}>
                                        <td className="px-3 py-2 font-num text-slate-700">{prettyDate(r.date)}</td>
                                        <td className="px-3 py-2 text-blue-950 font-semibold">{MARKET_META[r.market_key]?.name || r.market_key}</td>
                                        <td className="px-3 py-2 text-right result-digit text-blue-900">{r.fr ?? "—"}</td>
                                        <td className="px-3 py-2 text-right result-digit text-blue-900">{r.sr ?? "—"}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminShell>
    );
};

export default AdminManageResults;
